import React, { useState } from 'react';
import { Controller } from 'react-hook-form';
import { Select } from '../../../../CommonComponents/OPL/Select/Select/Select';
import { useBusinessForm } from './useBusinessForm';
import { UPSERT_INCOME_FORM_ID } from '../../components/UpsertIncomeModal/UpsertIncomeModal.meta';
import { IncomeData } from '../../useIncome';
import { InputNumberMask } from '../../../../CommonComponents/OPL/Input/InputNumberMask';
import { Button } from '../../../../CommonComponents/OPL/Button/Button';
import { IncomeModel } from '../../api/useIncomeQuery';
import { Box } from '../../../../CommonComponents/OPL/Box/Box';
import { Toggle } from '../../../../CommonComponents/OPL/Toggle/Toggle';
import { Alert } from '../../../../CommonComponents/OPL/Alert/Alert';
import { BusinessEmpty } from '../../tables/Business/Business.empty';
import { WorkflowComponentProps } from '../../../../Workflow/Workflow.meta';
import { ManagementStyle } from '../components/ManagementStyle/ManagementStyle';
import { AssetBucket } from '../components/AssetBucket/AssetBucket';

export interface Props {
  clientId: string;
  income: IncomeModel | null;
  data: IncomeData;
  onSuccess: () => void;
  workflowProps?: WorkflowComponentProps;
}

export const BusinessForm = ({ clientId, income, data, onSuccess, workflowProps }: Props) => {
  const {
    data: {
      formBag: { register, control, setValue, watch },
      defaultShowAdvanced,
      startYearOptions,
      endYearOptions,
      mutationBag,
    },
    actions: { onSubmit },
  } = useBusinessForm({ clientId, income, data, onSuccess });

  const [showAdvanced, setShowAdvanced] = useState(defaultShowAdvanced);

  const { Options: options } = data;
  const { BusinessAssetOption } = options;

  const [managementStyleValue, customBucketIndex] = watch([
    'orionManagementStyleId',
    'customBucketIndex',
  ]);

  if (BusinessAssetOption.length === 0) {
    return <BusinessEmpty clientId={clientId} workflowProps={workflowProps} />;
  }

  return (
    <form
      onSubmit={onSubmit}
      id={UPSERT_INCOME_FORM_ID}
      css={`
        display: flex;
        flex-direction: column;
        gap: 16px;
      `}
      aria-label="Business income form"
    >
      {mutationBag.error && (
        <Alert
          variant="error"
          message={mutationBag.error.message}
          onRequestClose={mutationBag.reset}
        />
      )}

      <Controller
        name="associatedAssetId"
        control={control}
        render={({ field: { value, onChange } }) => (
          <Select
            label="Business Asset"
            value={BusinessAssetOption.find(({ value: v }) => v === value)}
            options={BusinessAssetOption}
            onChange={option => onChange(option?.value)}
            isPortal
          />
        )}
      />

      <Controller
        control={control}
        name="amount"
        render={({ field: { onChange, ...field } }) => (
          <InputNumberMask
            label="Pre-tax Profit"
            decimalScale={2}
            allowNegative={false}
            leftIcon="far fa-dollar-sign"
            innerText="/year"
            onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
            {...field}
          />
        )}
      />

      <ManagementStyle
        control={control}
        options={options}
        fieldName="orionManagementStyleId"
        onChange={(value: number | null) => {
          setValue('customBucketIndex', value);
        }}
      />

      <AssetBucket
        control={control}
        fieldName="customBucketIndex"
        managementStyleValue={managementStyleValue}
        options={options}
      />

      <Button
        variant="link"
        leftIcon={showAdvanced ? 'fas fa-chevron-down' : 'fas fa-chevron-right'}
        onClick={() => setShowAdvanced(!showAdvanced)}
        aria-expanded={showAdvanced}
        size="small"
        mr="auto"
      >
        {showAdvanced ? 'Hide' : 'Show'} Advanced
      </Button>

      {showAdvanced && (
        <>
          <Controller
            control={control}
            name="incomeGrowth"
            render={({ field: { onChange, ...field } }) => (
              <InputNumberMask
                label="Income Growth"
                rightIcon="far fa-percent"
                decimalScale={2}
                allowNegative={false}
                onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
                tooltip='Enter the income growth rate applied to the Pre-tax Profit field. Leave this field blank to use the global "Income Growth Rate" found in Settings.'
                {...field}
              />
            )}
          />

          <Box
            css={`
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 16px;
            `}
          >
            <Controller
              name="startYear"
              control={control}
              render={({ field: { value, onChange } }) => (
                <Select
                  label="Start Year"
                  value={startYearOptions.find(({ value: v }) => v === value)}
                  options={startYearOptions}
                  onChange={option => onChange(option?.value)}
                  tooltip="Enter the year when the income will start. To start this income immediately, leave this field blank."
                  isPortal
                />
              )}
            />

            <Controller
              name="endYear"
              control={control}
              render={({ field: { value, onChange } }) => (
                <Select
                  label="End Year"
                  value={endYearOptions.find(({ value: v }) => v === value)}
                  options={endYearOptions}
                  onChange={option => {
                    onChange(option?.value);
                    setValue('endAtRetirement', option?.value === null);
                  }}
                  tooltip="Enter the year when the income will stop. If left blank, income will stop at sale year of business asset. If both fields left blank, income will stop at retirement."
                  isPortal
                />
              )}
            />
          </Box>

          <Toggle
            {...register('selfEmployed')}
            size="small"
            helperText="Applies SECA tax and deducts half the amount from pre-tax profit."
          >
            SECA Tax
          </Toggle>
        </>
      )}
    </form>
  );
};
